import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Building2, Clock, UserX, ShieldX, Link2Off, LogIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { apiPost } from '../utils/api';
import { toast } from 'react-hot-toast';
import { API_URL } from '../utils/config';
import { getToken as getAuthToken } from '../stores/authStore';

const ClientInvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [status, setStatus] = useState('loading');
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) loadInvitation();
  }, [token]);

  // Auto-accept after login redirect
  useEffect(() => {
    const shouldAutoAccept = localStorage.getItem('auto_accept_client_invitation');
    if (shouldAutoAccept === token && status === 'ready') {
      localStorage.removeItem('auto_accept_client_invitation');
      localStorage.removeItem('jpro_login_redirect');
      localStorage.removeItem('jpro_invite_flag_ts');
      handleAccept();
    }
  }, [status, token]);

  const loadInvitation = async () => {
    try {
      const response = await fetch(`${API_URL}/client_invitations/${token}`);
      if (!response.ok) {
        const data = await response.json();
        setStatus('error');
        setError(data.error || 'Invalid or expired invitation');
        return;
      }
      const data = await response.json();
      setInvitation(data.invitation);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setError('Failed to load invitation');
    }
  };

  const handleAccept = async () => {
    const jwtToken = getAuthToken();
    if (!jwtToken) {
      localStorage.setItem('auto_accept_client_invitation', token);
      localStorage.setItem('jpro_login_redirect', `/client-invitations/${token}`);
      localStorage.setItem('jpro_invite_flag_ts', Date.now().toString());
      navigate('/login', { state: { from: { pathname: `/client-invitations/${token}` } } });
      return;
    }

    setStatus('accepting');
    try {
      const response = await apiPost(`/client_invitations/${token}/accept`);
      if (response.ok) {
        const data = await response.json();
        setStatus('success');
        toast.success(data.message || 'Successfully joined!');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        const data = await response.json();
        setStatus('error');
        setError(data.error || 'Failed to accept invitation');
      }
    } catch (err) {
      setStatus('error');
      setError('Failed to accept invitation');
    }
  };

  const handleDecline = async () => {
    try {
      await apiPost(`/client_invitations/${token}/decline`);
    } catch {}
    navigate('/dashboard');
  };

  const isLoggedIn = !!getAuthToken();

  if (status === 'loading') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}`} />
          <p className={`font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const el = error.toLowerCase();
    let ErrorIcon = XCircle, errorTitle = 'Invitation Error', errorHint = null;
    if (el.includes('expired')) { ErrorIcon = Clock; errorTitle = 'Invitation Expired'; errorHint = 'Ask the manager to send a new invitation.'; }
    else if (el.includes('already') && el.includes('accepted')) { ErrorIcon = CheckCircle; errorTitle = 'Already Accepted'; }
    else if (el.includes('declined')) { ErrorIcon = UserX; errorTitle = 'Invitation Declined'; }
    else if (el.includes('revoked')) { ErrorIcon = ShieldX; errorTitle = 'Invitation Revoked'; }
    else if (el.includes('locked')) { ErrorIcon = ShieldX; errorTitle = 'Invitation Locked'; errorHint = 'Too many attempts. Contact the manager for a new link.'; }
    else if (el.includes('login') || el.includes('logged in')) { ErrorIcon = LogIn; errorTitle = 'Login Required'; errorHint = 'Please log in first, then try the invitation link again.'; }
    else if (el.includes('invalid') || el.includes('not found')) { ErrorIcon = Link2Off; errorTitle = 'Invalid Link'; }
    else if (el.includes('already belong')) { ErrorIcon = Building2; errorTitle = 'Already in Organization'; errorHint = 'You already belong to an organization.'; }

    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <ErrorIcon className={`w-16 h-16 mx-auto mb-4 ${ErrorIcon === CheckCircle ? 'text-blue-500' : 'text-red-500'}`} />
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{errorTitle}</h1>
          <p className={`font-rasa mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
          {errorHint && <p className={`font-rasa text-sm mb-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{errorHint}</p>}
          <button onClick={() => navigate('/dashboard')} className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-rasa font-semibold transition-colors">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Welcome to {invitation?.company_name}!
          </h1>
          <p className={`font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Redirecting you now...</p>
        </div>
      </div>
    );
  }

  // Ready state
  const inviterName = invitation.invited_by?.first_name
    ? `${invitation.invited_by.first_name} ${invitation.invited_by.last_name || ''}`.trim()
    : invitation.invited_by?.username;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`w-full max-w-md rounded-lg shadow-xl p-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <Building2 className={`w-8 h-8 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <h1 className={`text-2xl font-dm-serif mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            You're Invited!
          </h1>
        </div>

        <div className="mb-6">
          <p className={`text-center font-rasa mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            <strong>{inviterName}</strong> invited you to join:
          </p>

          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <h2 className={`font-dm-serif text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {invitation.company_name}
            </h2>
            <p className={`text-sm font-rasa mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Organization on JPRO
            </p>
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'}`}>
          <p className={`text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            You'll join as a <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Team Member</strong>
          </p>
          <p className={`text-xs font-rasa mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            Expires: {new Date(invitation.expires_at).toLocaleDateString()}
          </p>
        </div>

        {isLoggedIn ? (
          <>
            <button
              onClick={handleAccept}
              disabled={status === 'accepting'}
              className={`w-full py-3 rounded-lg font-rasa font-semibold mb-3 transition-colors ${
                status === 'accepting' ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {status === 'accepting' ? 'Accepting...' : 'Accept Invitation'}
            </button>
            <button
              onClick={handleDecline}
              className={`w-full py-2 rounded-lg font-rasa transition-colors ${
                theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-750' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Decline
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <button
              onClick={handleAccept}
              className="w-full py-3 rounded-lg font-rasa font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-colors"
            >
              Log In to Accept
            </button>
            <p className={`text-center text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Don't have an account?{' '}
              <Link
                to={`/signup?email=${encodeURIComponent(invitation.email || '')}&first_name=${encodeURIComponent(invitation.first_name || '')}&last_name=${encodeURIComponent(invitation.last_name || '')}&return=${encodeURIComponent(`/client-invitations/${token}`)}`}
                className="text-blue-500 hover:text-blue-600 font-semibold"
              >
                Create one
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientInvitationPage;
